import React, { useState, useRef, useEffect } from "react";
import { Badge } from "antd";
import FeatherIcon from "feather-icons-react";
import { useHistory } from "react-router-dom";
import { useUnreadMessages } from "../../../hooks/useUnreadMessages";
import { markRead } from "../../../config/api/messengerService";
import { fetchFilteredQuotes } from "../../../config/api/apiService";

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// Notifications only carry a job number, not the quote's own id that /admin/quotes/edit/:id
// routes on — this reuses the existing filtered-quotes lookup (same one the Quotes list's
// "Filter by job #" box calls) instead of adding a lookup-by-job-number endpoint.
async function findQuoteIdByJobNumber(jobNumber) {
  const result = await fetchFilteredQuotes({ key: "jobNumber", type: "contains", value: jobNumber }, 1, null);
  const pair = result?.data?.[0];
  return pair?.quote?.quoteId || pair?.estimation?.estimationId || null;
}

function MessengerNotificationBell() {
  const { hasUnread, items, refresh } = useUnreadMessages();
  const [open, setOpen] = useState(false);
  const [navigatingChannel, setNavigatingChannel] = useState(null);
  const containerRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleSelect = async (item) => {
    setNavigatingChannel(item.channelName);
    try {
      const [quoteId] = await Promise.all([
        findQuoteIdByJobNumber(item.jobId),
        markRead(item.channelName).catch(() => {}),
      ]);
      setOpen(false);
      refresh();
      if (quoteId) {
        history.push(`/admin/quotes/edit/${quoteId}`, { activeTab: "7" });
      }
    } finally {
      setNavigatingChannel(null);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", marginRight: 15 }}>
      <div
        role="button"
        onClick={() => setOpen((o) => !o)}
        className="notification"
        style={{ cursor: "pointer" }}
      >
        <Badge dot={hasUnread} offset={[-8, -5]}>
          <span className="head-example">
            <FeatherIcon icon="bell" size={20} />
          </span>
        </Badge>
      </div>

      {open && (
        <div
          style={{
            position: "absolute", top: 34, right: 0, width: 340, maxHeight: 420, overflowY: "auto",
            background: "#fff", border: "1px solid #f0f0f0", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 1000,
          }}
        >
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #f0f0f0", fontSize: 13, fontWeight: 600 }}>
            Notifications
          </div>
          {items.length === 0 ? (
            <p style={{ fontSize: 13, color: "#8c8c8c", padding: "18px 14px", margin: 0, textAlign: "center" }}>
              No new messages.
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.channelName}
                role="button"
                onClick={() => handleSelect(item)}
                style={{
                  padding: "10px 14px", borderBottom: "1px solid #f5f5f5", cursor: "pointer",
                  opacity: navigatingChannel === item.channelName ? 0.6 : 1,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Job #{item.jobId}</span>
                  <span style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>{timeAgo(item.notifiedAt)}</span>
                </div>
                {item.senderEmail && (
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2 }}>{item.senderEmail}</div>
                )}
                <div style={{ fontSize: 13, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.preview}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default MessengerNotificationBell;
